import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getShopifyAnalytics } from '../api/commerceAnalytics';
import type { ShopifyAnalytics } from '../types/commerceAnalytics';
import styles from './CommerceAnalytics.module.css';

/**
 * Commerce Analytics page (Shopify).
 *
 * Daily revenue + order-count time series from the Shopify Admin orders API,
 * via `/api/admin/shopify/analytics/`.
 *
 * Sessions / conversion-rate panels were removed: the Admin API's ShopifyQL
 * surface exposes only the sales/orders/customers datasets (no `sessions`),
 * so storefront traffic isn't reliably available here. Acquisition/traffic
 * analytics live in GA4/Meta instead. The backend still returns those blocks
 * (degraded), but the dashboard no longer renders them.
 *
 * Mirrors the Growth Charts page conventions: single `days` selector, one
 * network call per window, Recharts area charts, dark theme.
 */

const WINDOWS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '1y', days: 365 },
];

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtMoney(n: number | null | undefined, currency: string): string {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: n >= 1000 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

function tickInterval(pointCount: number): number {
  if (pointCount <= 12) return 0;
  return Math.max(Math.floor(pointCount / 12) - 1, 0);
}

interface ChartPoint {
  label: string;
  value: number;
}

/**
 * Minimal shape the chart needs. Revenue/orders/sessions/conversion all
 * carry `labels` + `data` but differ in their summary fields, so we only
 * depend on the common parts here.
 */
type ChartableSeries = { labels: string[]; data: number[] };

function seriesToPoints(series: ChartableSeries | undefined): ChartPoint[] {
  if (!series || !series.labels || !series.data) return [];
  const n = Math.min(series.labels.length, series.data.length);
  const out: ChartPoint[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ label: series.labels[i], value: series.data[i] });
  }
  return out;
}

type ValueFormatter = (value: number) => string;

interface MetricChartProps {
  title: string;
  headlineValue: string;
  subLabel: string;
  color: string;
  series: ChartableSeries | undefined;
  emptyMessage: string;
  valueFormatter: ValueFormatter;
  notice?: string | null;
}

function MetricChart({
  title,
  headlineValue,
  subLabel,
  color,
  series,
  emptyMessage,
  valueFormatter,
  notice,
}: MetricChartProps) {
  const points = useMemo(() => seriesToPoints(series), [series]);
  const interval = tickInterval(points.length);
  const gradientId = useMemo(
    () => `commerce-grad-${title.replace(/\s+/g, '-').toLowerCase()}`,
    [title]
  );

  const empty = points.length === 0 || points.every((p) => p.value === 0);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div>
          <span className={styles.sectionTotal}>{headlineValue}</span>
          <span className={styles.sectionSubtotal}>{subLabel}</span>
        </div>
      </div>
      {notice ? (
        <div className={styles.empty}>{notice}</div>
      ) : empty ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <div className={styles.chartWrap}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart
              data={points}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.06)"
              />
              <XAxis
                dataKey="label"
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                interval={interval}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                width={56}
                allowDecimals={false}
                tickFormatter={(v) =>
                  valueFormatter(typeof v === 'number' ? v : Number(v))
                }
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: '#e5e7eb' }}
                formatter={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [
                    Number.isFinite(n) ? valueFormatter(n) : String(value),
                    title,
                  ];
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function CommerceAnalytics() {
  const [days, setDays] = useState<number>(90);
  const [data, setData] = useState<ShopifyAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowDays: number, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getShopifyAnalytics(windowDays, refresh);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to load Shopify analytics';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const currency = data?.summary.currency ?? 'USD';
  const moneyFmt = useCallback(
    (n: number) => fmtMoney(n, currency),
    [currency]
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Commerce Analytics</h1>
          <p className={styles.subtitle}>
            Shopify daily revenue and order counts across the rolling window,
            summed from the Admin orders API.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatRelativeTime(lastUpdated)}
          </span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(days, true)}
            disabled={loading}
          >
            <span
              className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
            >
              ↻
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={`${styles.pill} ${days === w.days ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {data?.orders_note && (
        <div className={styles.warning}>{data.orders_note}</div>
      )}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.total_revenue, currency)}
          </div>
          <div className={styles.statLabel}>Total revenue ({days}d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(data?.summary.total_orders)}
          </div>
          <div className={styles.statLabel}>Orders ({days}d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtMoney(data?.summary.aov, currency)}
          </div>
          <div className={styles.statLabel}>Average order value</div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading Shopify analytics…</div>
      ) : data ? (
        <div className={styles.chartsGrid}>
          <MetricChart
            title="Daily Revenue"
            headlineValue={fmtMoney(data.summary.total_revenue, currency)}
            subLabel={`total · ${currency}`}
            color="#22c55e"
            series={data.revenue}
            emptyMessage="No revenue recorded in this window."
            valueFormatter={moneyFmt}
          />
          <MetricChart
            title="Daily Orders"
            headlineValue={fmtNumber(data.summary.total_orders)}
            subLabel="orders"
            color="#3b82f6"
            series={data.orders}
            emptyMessage="No orders recorded in this window."
            valueFormatter={(n) => fmtNumber(n)}
          />
        </div>
      ) : null}

      <div className={styles.notes}>
        <h2 className={styles.notesTitle}>Notes</h2>
        <ul>
          <li>
            Revenue is summed from Shopify orders'{' '}
            <code>currentTotalPrice</code> (test and cancelled orders
            excluded), grouped by order creation date in UTC.
          </li>
          <li>
            Figures are cached server-side for ~30 minutes per window. Use{' '}
            <strong>Refresh</strong> to pull live data.
          </li>
          <li>
            <strong>Traffic &amp; conversion</strong> (sessions, sources,
            campaigns) aren't shown here — Shopify's Admin API doesn't expose a
            sessions dataset. Use GA4/Meta for acquisition analytics, and the{' '}
            <strong>Checkout Funnel</strong> page for purchase-flow drop-off.
          </li>
        </ul>
      </div>
    </div>
  );
}
